// Phase 5 · Autonomy Dial.
// Per-stage dial with four levels and hard ceilings. Changes write an
// audit row. Pause-everything acts as a kill switch for the scheduler
// and (via effective_level) collapses every stage to "manual".
import React, { useCallback, useEffect, useState } from "react";

import Shell from "../layout/Shell";
import { api, formatApiError } from "../lib/api";
import { ErrorBanner, LoadingCard, Pill } from "../lib/ui";

const STAGE_META = {
  monthly_goal:    { label: "Monthly goal",         help: "Picks the North-Star metric and theme for the month." },
  weekly_plan:     { label: "Weekly plan",          help: "Builds the four weekly themes and the slate of posts." },
  post_drafts:     { label: "Post drafts",          help: "Writes the headlines, bodies and CTAs for each slot." },
  posting:         { label: "Posting",              help: "Publishes approved posts to FB / IG." },
  waitlist_emails: { label: "Waitlist emails",      help: "Drips the welcome + follow-up emails to new signups." },
  faq_replies:     { label: "FAQ replies",          help: "Answers pricing / launch / availability questions in the Inbox." },
  other_replies:   { label: "Other replies",        help: "Everything in the Inbox that isn't FAQ or always-manual." },
  lessons:         { label: "Lessons",              help: "Promotes insights from the Learning Loop into the Vault." },
  new_claims:      { label: "New claims",           help: "Adds brand-new C-n facts (allowed claims). Manual only — no higher level is permitted; a human must enter the claim in the Vault." },
};

const LEVEL_META = {
  manual:           { label: "Manual",                 blurb: "Nothing happens until you act." },
  draft_for_me:     { label: "Draft for me",            blurb: "Brand OS prepares a draft; you approve." },
  auto_with_veto:   { label: "Auto with veto",          blurb: "Fires after a veto window; you can cancel inside the window." },
  fully_automatic:  { label: "Fully automatic",         blurb: "Fires immediately on Guardian pass." },
};

const LEVELS_ORDER = ["manual", "draft_for_me", "auto_with_veto", "fully_automatic"];

export default function AutonomyDial() {
  const [state, setState] = useState({ loading: true, error: null });
  const [aut, setAut] = useState(null);
  const [busy, setBusy] = useState(false);
  const [flash, setFlash] = useState(null);
  const [reason, setReason] = useState("");

  const load = useCallback(async () => {
    setState({ loading: true, error: null });
    try {
      const r = await api.get("/autonomy");
      setAut(r.data);
      setState({ loading: false, error: null });
    } catch (e) {
      setState({ loading: false, error: formatApiError(e) });
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  async function patch(body) {
    setBusy(true);
    try {
      const r = await api.patch("/autonomy", { ...body, reason: reason || null });
      setAut(r.data);
      setFlash("Saved · audit row written.");
    } catch (e) {
      setState((s) => ({ ...s, error: formatApiError(e) }));
    } finally {
      setBusy(false);
      setReason("");
    }
  }

  function setStage(stage, level) {
    patch({ stages: { [stage]: level } });
  }

  if (state.loading) return <Shell title="Autonomy"><LoadingCard label="Loading autonomy…" /></Shell>;
  const ceilings = aut?.stage_ceilings || {};
  const levels = aut?.stage_levels || LEVELS_ORDER;
  const stages = aut?.stages || {};

  return (
    <Shell title="Autonomy Dial">
      <div className="bo-page" data-testid="autonomy-page">
        <section className="bo-card">
          <div className="bo-eyebrow">Phase 5 · Autonomy</div>
          <h1 className="bo-serif" style={{ fontSize: 26, margin: "6px 0 8px" }}>Autonomy Dial</h1>
          <p style={{ color: "var(--bo-muted)", maxWidth: 780, lineHeight: 1.55 }}>
            Four levels per stage: Manual, Draft for me, Auto with veto, Fully
            automatic. Hard ceilings prevent brand-risky stages from going
            fully automatic. <strong>Pause everything</strong> instantly
            demotes every stage to Manual.
          </p>
          <ErrorBanner error={state.error} />
          {flash && (
            <div className="bo-flash" style={{ marginTop: 12, padding: 10, borderRadius: 8, background: "var(--bo-accent-soft)" }}>
              {flash}
            </div>
          )}
          <div style={{ display: "flex", gap: 10, alignItems: "center", marginTop: 14, flexWrap: "wrap" }}>
            <button
              className="bo-btn bo-btn-primary"
              onClick={() => patch({ pause_everything: !aut.pause_everything })}
              disabled={busy}
              data-testid="autonomy-pause-toggle"
              style={aut?.pause_everything ? { background: "var(--bo-danger)" } : undefined}
            >
              {aut?.pause_everything ? "Resume (currently PAUSED)" : "Pause everything"}
            </button>
            <label style={{ fontSize: 13 }}>
              Veto window hours:{" "}
              <input
                type="number"
                min={0}
                max={168}
                defaultValue={aut?.veto_window_hours}
                onBlur={(e) => {
                  const n = Number(e.target.value);
                  if (!Number.isNaN(n) && n !== aut.veto_window_hours) patch({ veto_window_hours: n });
                }}
                style={{ width: 70, padding: "6px 8px", borderRadius: 6, border: "1px solid var(--bo-border)" }}
                data-testid="autonomy-veto-hours"
              />
            </label>
            <label style={{ fontSize: 13, display: "flex", alignItems: "center", gap: 6 }}>
              <input
                type="checkbox"
                checked={!!aut?.daily_digest_enabled}
                onChange={(e) => patch({ daily_digest_enabled: e.target.checked })}
                data-testid="autonomy-digest-toggle"
              />
              Daily digest (7:00 AM ET)
            </label>
            <input
              type="text"
              placeholder="reason (optional, logged)"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              style={{ flex: "1 1 240px", padding: "8px 10px", borderRadius: 6, border: "1px solid var(--bo-border)", fontSize: 13 }}
            />
          </div>
        </section>

        <section style={{ marginTop: 20, display: "grid", gap: 12 }}>
          {Object.entries(STAGE_META).map(([stage, meta]) => {
            const level = stages[stage] || "manual";
            const ceiling = ceilings[stage] || "fully_automatic";
            const ceilingIdx = levels.indexOf(ceiling);
            return (
              <div key={stage} className="bo-card" data-testid={`autonomy-stage-${stage}`}>
                <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
                  <div style={{ flex: "1 1 240px" }}>
                    <div style={{ fontFamily: "var(--bo-serif)", fontSize: 17 }}>{meta.label}</div>
                    <div style={{ color: "var(--bo-muted)", fontSize: 13 }}>{meta.help}</div>
                  </div>
                  <Pill tone="muted">ceiling: {LEVEL_META[ceiling]?.label}</Pill>
                </div>
                <div style={{ display: "flex", gap: 6, marginTop: 10, flexWrap: "wrap" }}>
                  {levels.map((lv, i) => {
                    const disabled = i > ceilingIdx || busy;
                    const active = lv === level;
                    return (
                      <button
                        key={lv}
                        className={`bo-btn ${active ? "bo-btn-primary" : "bo-btn-ghost"}`}
                        onClick={() => setStage(stage, lv)}
                        disabled={disabled}
                        title={LEVEL_META[lv].blurb + (disabled && !active ? " (above ceiling)" : "")}
                        data-testid={`autonomy-stage-${stage}-${lv}`}
                        style={disabled && !active ? { opacity: 0.5 } : undefined}
                      >
                        {LEVEL_META[lv].label}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </section>
      </div>
    </Shell>
  );
}
